<template>
  <div
    class="ttp"
    @click.stop
  >
    <div class="ttp-head">
      <p class="ttp-title">
        {{ title }}
      </p>
      <input
        v-model="query"
        type="text"
        class="ttp-search"
        :placeholder="unitsSelectable ? 'Buscar unidade ou equipe…' : 'Buscar equipe…'"
      >
    </div>

    <div
      ref="list"
      class="ttp-list custom-scroll"
    >
      <button
        v-if="allLabel"
        type="button"
        class="ttp-team ttp-all"
        :class="{ 'is-selected': isAllChosen }"
        @click="pickAll"
      >
        <span
          class="ttp-check"
          :class="{ 'is-on': isAllChosen }"
        />
        <span class="ttp-team-name">{{ allLabel }}</span>
      </button>

      <team-tree-branch
        :nodes="visibleTree"
        tag="button"
        :is-chosen="isChosen"
        :is-covered="isCovered"
        :is-selectable="isSelectable"
        :units-selectable="unitsSelectable"
        @pick="pick"
      >
        <template #before="{ node }">
          <span
            class="ttp-check"
            :class="{
              'is-on': isChosen(node.id) && !isCovered(node.id),
              'is-covered': isCovered(node.id)
            }"
          />
        </template>
        <template #after="{ node }">
          <span
            v-if="!multiple && showCurrent && String(node.id) === String(currentId)"
            class="ttp-cur"
          >atual</span>
        </template>
      </team-tree-branch>

      <p
        v-if="visibleTree.length === 0"
        class="ttp-empty"
      >
        {{ unitsSelectable ? "Nenhuma unidade ou equipe encontrada." : "Nenhuma equipe encontrada." }}
      </p>
    </div>

    <div
      v-if="!immediate"
      class="ttp-foot"
    >
      <button
        type="button"
        class="ttp-btn ttp-ghost"
        @click="$emit('cancel')"
      >
        Cancelar
      </button>
      <button
        type="button"
        class="ttp-btn ttp-primary"
        :disabled="!canConfirm || saving"
        @click="confirm"
      >
        {{ saving ? "Salvando…" : "Confirmar" }}
      </button>
    </div>
  </div>
</template>

<script>
import TeamTreeBranch from "./TeamTreeBranch.vue";

export default {
  components: { "team-tree-branch": TeamTreeBranch },
  props: {
    tree: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: false },
    currentId: { default: null },
    initialSelected: { type: Array, default: () => [] },
    title: { type: String, default: "Trocar equipe" },
    saving: { type: Boolean, default: false },
    immediate: { type: Boolean, default: false },
    allLabel: { type: String, default: "" },
    unitsSelectable: { type: Boolean, default: true },
    showCurrent: { type: Boolean, default: true },
    coverDescendants: { type: Boolean, default: false },
    cascadeDescendants: { type: Boolean, default: false },
    allowDeselect: { type: Boolean, default: false },
    allChosen: { type: Boolean, default: null },
    isSelectable: { type: Function, default: () => true }
  },
  emits: ["select", "select-all", "cancel"],
  data() {
    return {
      query: "",
      chosen: this.initialChosen()
    };
  },
  computed: {
    isAllChosen() {
      if (this.allChosen !== null) return this.allChosen;

      return this.chosen.length === 0;
    },
    canConfirm() {
      if (this.multiple) {
        return (
          this.chosen.length > 0 &&
          !this.sameSet(this.chosen, this.initialSelected)
        );
      }
      return (
        this.chosen.length === 1 &&
        String(this.chosen[0]) !== String(this.currentId)
      );
    },
    coveredIds() {
      const marks = this.coverDescendants || this.cascadeDescendants;
      if (!marks || this.chosen.length === 0) return [];

      const covered = [];

      const collect = (nodes) => {
        (nodes || []).forEach((node) => {
          covered.push(String(node.id));
          collect(node.children);
        });
      };

      const walk = (nodes) => {
        (nodes || []).forEach((node) => {
          if (this.isChosen(node.id)) collect(node.children);
          else walk(node.children);
        });
      };

      walk(this.tree);

      return covered;
    },
    visibleTree() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.tree;
      const filter = (nodes) =>
        (nodes || []).reduce((out, node) => {
          const children = filter(node.children);
          const selfMatch = (node.name || "").toLowerCase().includes(q);
          if (selfMatch || children.length) {
            out.push({
              ...node,
              children: selfMatch ? node.children : children
            });
          }
          return out;
        }, []);
      return filter(this.tree);
    }
  },
  mounted() {
    this.scrollToChosen();
  },
  methods: {
    scrollToChosen() {
      const list = this.$refs.list;
      const selected = list && list.querySelector(".ttb-row.is-selected");
      if (!selected) return;

      const listBox = list.getBoundingClientRect();
      const selectedBox = selected.getBoundingClientRect();
      list.scrollTop += selectedBox.top - listBox.top - (listBox.height - selectedBox.height) / 2;
    },
    initialChosen() {
      if (this.multiple) return this.initialSelected.slice();

      return this.currentId == null || this.currentId === ""
        ? []
        : [this.currentId];
    },
    isCovered(id) {
      return this.coveredIds.includes(String(id));
    },
    isChosen(id) {
      return this.chosen.some((c) => String(c) === String(id));
    },
    descendantIds(id) {
      const found = [];

      const collect = (nodes) => {
        (nodes || []).forEach((node) => {
          found.push(node.id);
          collect(node.children);
        });
      };

      const walk = (nodes) => {
        for (const node of nodes || []) {
          if (String(node.id) === String(id)) {
            collect(node.children);
            return true;
          }

          if (walk(node.children)) return true;
        }

        return false;
      };

      walk(this.tree);

      return found;
    },
    pick(id) {
      if (this.multiple) {
        const affected = this.cascadeDescendants
          ? [id, ...this.descendantIds(id)]
          : [id];
        const isOn = this.isChosen(id);

        this.chosen = this.chosen.filter(
          (c) => !affected.some((a) => String(a) === String(c))
        );

        if (!isOn) this.chosen.push(...affected);
      } else if (this.allowDeselect && this.isChosen(id)) {
        this.chosen = [];
        if (this.immediate) this.$emit("select", null);
        return;
      } else {
        this.chosen = [id];
      }
      // Modo filtro (única): aplica na hora, sem Confirmar.
      if (this.immediate && !this.multiple) this.$emit("select", id);
    },
    pickAll() {
      this.chosen = [];
      this.$emit("select", null);
      this.$emit("select-all");
    },
    sameSet(a, b) {
      if (a.length !== b.length) return false;
      const sb = new Set(b.map(String));
      return a.every((x) => sb.has(String(x)));
    },
    confirm() {
      if (!this.canConfirm || this.saving) return;
      this.$emit(
        "select",
        this.multiple ? this.chosen.slice() : this.chosen[0]
      );
    }
  }
};
</script>

<style scoped>
.ttp {
  width: 320px;
  max-width: 88vw;
  background: #ffffff;
  border: 1px solid #e5e2ef;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(36, 31, 51, 0.18);
  overflow: hidden;
}
.ttp-head {
  padding: 13px 15px 12px;
  border-bottom: 1px solid #eceaf3;
}
.ttp-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 640;
  color: #3a3a3a;
}
.ttp-search {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e5e2ef;
  background: #faf9fd;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 13px;
  color: #3a3a3a;
  outline: none;
}
.ttp-search:focus {
  border-color: #9a397d;
}
.ttp-list {
  padding: 6px 4px 4px;
  max-height: 320px;
  overflow-y: auto;
}
.ttp-team {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;
  padding: 9px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #3a3a3a;
  font-family: inherit;
}
.ttp-team:hover {
  background: #faf9fd;
}
.ttp-team.is-selected {
  background: rgba(79, 63, 130, 0.1);
}
.ttp-team.is-selected .ttp-team-name {
  color: #4f3f82;
  font-weight: 600;
}
.ttp-team-name {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ttp-all {
  border-bottom: 1px solid #f0eef7;
  border-radius: 0;
  margin-bottom: 2px;
}
.ttp-check {
  width: 16px;
  height: 16px;
  border-radius: 5px;
  border: 1.5px solid #d9d5e6;
  flex: none;
  position: relative;
  background: #ffffff;
}
.ttp-check.is-covered {
  border-color: #c4bdd8;
  background: #c4bdd8;
}
.ttp-check.is-covered::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.ttp-check.is-on {
  border-color: #4f3f82;
  background: #4f3f82;
}
.ttp-check.is-on::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.ttp-cur {
  font-size: 10px;
  color: #9a397d;
  background: rgba(154, 57, 125, 0.1);
  padding: 1px 8px;
  border-radius: 999px;
  flex: none;
}
.ttp-empty {
  padding: 18px;
  text-align: center;
  color: #9a9a9a;
  font-size: 13px;
  margin: 0;
}
.ttp-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 11px 15px;
  border-top: 1px solid #eceaf3;
  background: #faf9fd;
}
.ttp-btn {
  border: none;
  border-radius: 9px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.ttp-ghost {
  background: transparent;
  color: #6b6680;
}
.ttp-primary {
  background: #9a397d;
  color: #ffffff;
}
.ttp-primary:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
